import { Link } from 'react-router-dom'
import { cn } from '@/lib/utils'
import { useI18n } from '@/i18n'
import { Badge } from '@/components/ui/Badge'
import { highlight } from '@/lib/search'
import type { Watch } from '@/types'

/**
 * One piece in a grid. No card border, no shadow — the photograph is the card.
 * Everything below it is set quietly so a wall of twelve reads as a collection
 * rather than twelve competing adverts.
 */
export function WatchCard({
  watch,
  className,
  query = '',
}: {
  watch: Watch
  className?: string
  /** The search that produced this card — its words are marked in the name. */
  query?: string
}) {
  const { t, tf, formatPrice } = useI18n()
  const sold = watch.status === 'sold'
  const reserved = watch.status === 'reserved'

  return (
    <Link
      to={`/collection/${watch.id}`}
      data-upbo-id={`watch-card-${watch.id}`}
      data-upbo-type="card"
      data-upbo-label={`Watch card — ${watch.title}`}
      className={cn('group flex flex-col', className)}
    >
      <div className="relative overflow-hidden bg-surface-2 rounded-squircle aspect-[4/5]">
        <img
          src={watch.image}
          alt={tf(watch, 'title')}
          loading="lazy"
          className={cn('img-zoom h-full w-full object-cover', sold && 'opacity-55 grayscale')}
        />

        {(sold || reserved) && (
          <span className="absolute top-3 left-3">
            <Badge tone={sold ? 'neutral' : 'accent'} size="sm">
              {t(`status.${watch.status}`)}
            </Badge>
          </span>
        )}
      </div>

      <div className="pt-4 flex flex-col gap-1.5">
        <span className="text-[10px] uppercase tracking-[0.16em] text-text-faint">{watch.maison}</span>
        <h3 className="font-display text-[22px] leading-tight text-text group-hover:text-primary transition-colors">
          {highlight(tf(watch, 'title'), query).map((seg, i) =>
            seg.hit ? (
              <mark key={i} className="bg-transparent text-primary">
                {seg.text}
              </mark>
            ) : (
              <span key={i}>{seg.text}</span>
            ),
          )}
        </h3>
        <div className="flex items-baseline gap-2.5 mt-0.5">
          <span className="text-sm text-text">{formatPrice(watch.price)}</span>
          {watch.old_price > watch.price && (
            <span className="text-[12px] text-text-faint line-through">{formatPrice(watch.old_price)}</span>
          )}
        </div>
      </div>
    </Link>
  )
}
